import { useEffect, useRef, useState } from "react";
import { FiAlertCircle, FiFilter, FiX } from "react-icons/fi";
import { MdAttachMoney } from "react-icons/md";
import * as XLSX from "xlsx";
import BotaoRelatorio from "../../../components/relatorios/BotaoRelatorio";
import CabecalhoRelatorio from "../../../components/relatorios/CabecalhoRelatorio";
import Cartao from "../../../components/relatorios/Cartao";
import CartaoEstatistica from "../../../components/relatorios/CartaoEstatistica";
import EstadoVazio from "../../../components/relatorios/EstadoVazio";
import { api, listarTudo } from "../../../services/api";
import { exportarRelatorioPDF, formatarPeriodoFiltro } from "../../../utils/relatorioPdf";

const RelatorioCustoPorAtividade = () => {
  const [dados, setDados] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");

  const [tiposAtividade, setTiposAtividade] = useState([]);
  const [talhoes, setTalhoes] = useState([]);
  const [fazendas, setFazendas] = useState([]);

  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = {
    tiposAtividade: useRef(null),
    talhoes: useRef(null),
    fazendas: useRef(null)
  };

  const [filtros, setFiltros] = useState({
    tiposAtividade: [],
    talhoes: [],
    fazendas: [],
    data_inicio: "",
    data_fim: ""
  });

  useEffect(() => {
    carregarOpcoes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && dropdownRefs[openDropdown]?.current && !dropdownRefs[openDropdown].current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const carregarOpcoes = async () => {
    try {
      const [tiposRes, talhoesRes, fazendasRes] = await Promise.all([
        listarTudo("/agricultura/tipoatividade/"),
        api.get("/fazendas/talhoes/"),
        api.get("/fazendas/fazendas/")
      ]);

      setTiposAtividade(Array.isArray(tiposRes.data) ? tiposRes.data : tiposRes.data.results || []);
      setTalhoes(talhoesRes.data.results || []);
      setFazendas(fazendasRes.data.results || []);
    } catch (error) {
      console.error("Erro ao carregar opções:", error);
      setTiposAtividade([]);
      setTalhoes([]);
      setFazendas([]);
    }
  };

  const handleMultiSelect = (campo, id) => {
    setFiltros(prev => {
      const current = prev[campo] || [];
      const updated = current.includes(id)
        ? current.filter(item => item !== id)
        : [...current, id];
      return { ...prev, [campo]: updated };
    });
  };

  const handleSelectAll = (campo, options) => {
    if (Array.isArray(options)) {
      const allIds = options.map(opt => opt.id);
      setFiltros(prev => ({ ...prev, [campo]: allIds }));
    }
  };

  const handleClearAll = (campo) => {
    setFiltros(prev => ({ ...prev, [campo]: [] }));
  };

  const handleChange = (e) => {
    setFiltros({
      ...filtros,
      [e.target.name]: e.target.value
    });
  };

  const buscarDados = async () => {
    setCarregando(true);
    setErro("");

    try {
      const params = {};

      if (filtros.tiposAtividade && filtros.tiposAtividade.length > 0) {
        params.tipos_atividade = filtros.tiposAtividade.join(",");
      }
      if (filtros.talhoes && filtros.talhoes.length > 0) {
        params.talhoes = filtros.talhoes.join(",");
      }
      if (filtros.fazendas && filtros.fazendas.length > 0) {
        params.fazendas = filtros.fazendas.join(",");
      }
      if (filtros.data_inicio) {
        params.data_inicio = filtros.data_inicio;
      }
      if (filtros.data_fim) {
        params.data_fim = filtros.data_fim;
      }

      console.log("🌐 URL:", `/relatorios/financeiro/custo-atividade/?${new URLSearchParams(params)}`);

      const response = await api.get("/relatorios/financeiro/custo-atividade/", {
        params: params
      });

      console.log("🔍 Dados recebidos:", response.data);
      setDados(response.data);
      setShowModal(true);
    } catch (error) {
      console.error(error);
      setErro("Erro ao gerar o relatório. Verifique os filtros e tente novamente.");
    } finally {
      setCarregando(false);
    }
  };

  const limparFiltros = () => {
    setFiltros({
      tiposAtividade: [],
      talhoes: [],
      fazendas: [],
      data_inicio: "",
      data_fim: ""
    });
  };

  const formatarMoeda = (valor) => {
    if (!valor && valor !== 0) return "-";
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
  };

  const formatarHoras = (horas) => {
    if (!horas && horas !== 0) return "-";
    return `${horas} h`;
  };

  const exportarExcel = () => {
    if (!dados || dados.length === 0) {
      alert("Nao ha dados para exportar!");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(dados);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Custo por Atividade"
    );

    XLSX.writeFile(workbook, "relatorio_custo_atividade.xlsx");
  };

  const exportarPDF = async () => {
    if (!dados || dados.length === 0) {
      alert("⚠️ Não há dados para exportar!");
      return;
    }

    // Linha de total ao final do PDF — mesmo padrão já usado no relatório de
    // Custo por Hectare (o utilitário compartilhado de PDF não tem esse
    // conceito, então ela entra como mais uma linha de dados; só a coluna
    // DATA recebe o rótulo "TOTAL").
    let totalInsumos = 0;
    let totalMaquina = 0;
    let totalMaoObra = 0;
    let totalGeral = 0;
    dados.forEach((item) => {
      totalInsumos += parseFloat(item.custo_insumos || 0);
      totalMaquina += parseFloat(item.custo_maquina || 0);
      totalMaoObra += parseFloat(item.custo_mao_obra || 0);
      totalGeral += parseFloat(item.custo_total || 0);
    });
    const linhaTotal = {
      data: "TOTAL",
      atividade: "",
      talhao: "",
      recursos: "",
      tempo_gasto: "",
      custo_insumos: totalInsumos,
      custo_maquina: totalMaquina,
      custo_mao_obra: totalMaoObra,
      custo_total: totalGeral,
    };

    await exportarRelatorioPDF({
      titulo: "Relatório de Custo por Atividade",
      colunas: [
        { key: "data", label: "DATA" },
        { key: "atividade", label: "ATIVIDADE" },
        { key: "talhao", label: "TALHÃO" },
        { key: "recursos", label: "RECURSOS UTILIZADOS" },
        { key: "tempo_gasto", label: "TEMPO GASTO" },
        { key: "custo_insumos", label: "CUSTO INSUMOS" },
        { key: "custo_maquina", label: "CUSTO MÁQUINA" },
        { key: "custo_mao_obra", label: "CUSTO MÃO DE OBRA" },
        { key: "custo_total", label: "CUSTO TOTAL" },
      ],
      dados: [...dados, linhaTotal],
      periodo: formatarPeriodoFiltro(filtros.data_inicio, filtros.data_fim),
      formatarValor: (valor, coluna) =>
        coluna.key.startsWith("custo_") ? formatarMoeda(valor) : (valor ?? "-"),
    });
  };

  const imprimir = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Relatorio de Custo por Atividade</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; font-size: 18px; }
            .header-info { margin-bottom: 20px; }
            .periodo { background: #e8f4f8; padding: 10px; border-radius: 5px; margin-bottom: 20px; }
            .stats { display: flex; gap: 20px; margin-bottom: 20px; flex-wrap: wrap; }
            .stat-card { background: #f0f0f0; padding: 15px; border-radius: 8px; text-align: center; min-width: 150px; }
            .stat-card .valor { font-size: 24px; font-weight: bold; color: #2c3e50; }
            .stat-card .label { font-size: 12px; color: #666; margin-top: 5px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 10px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .footer { margin-top: 20px; font-size: 10px; color: #666; text-align: center; }
            @media print {
              body { margin: 0; padding: 20px; }
              .stat-card { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <h1>Relatorio de Custo por Atividade</h1>
          <div class="header-info">
            <p><strong>Gerado em:</strong> ${new Date().toLocaleString()}</p>
            <p><strong>Total de registros:</strong> ${dados.length}</p>
          </div>

          ${filtros.data_inicio || filtros.data_fim ? `
          <div class="periodo">
            <strong>📅 Periodo analisado:</strong>
            <p>${filtros.data_inicio || 'Sem data inicial'} a ${filtros.data_fim || 'Sem data final'}</p>
          </div>
          ` : ''}

          <div class="stats">
            <div class="stat-card">
              <div class="valor">${dados.length}</div>
              <div class="label">Total Atividades</div>
            </div>
            <div class="stat-card">
              <div class="valor">${formatarMoeda(dados.reduce((acc, item) => acc + (parseFloat(item.custo_total) || 0), 0))}</div>
              <div class="label">Custo Total</div>
            </div>
            <div class="stat-card">
              <div class="valor">${formatarMoeda(dados.reduce((acc, item) => acc + (parseFloat(item.custo_insumos) || 0), 0))}</div>
              <div class="label">Total Insumos</div>
            </div>
            <div class="stat-card">
              <div class="valor">${formatarMoeda(dados.reduce((acc, item) => acc + (parseFloat(item.custo_mao_obra) || 0), 0))}</div>
              <div class="label">Total Mão de Obra</div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>DATA</th>
                <th>ATIVIDADE</th>
                <th>TALHAO</th>
                <th>RECURSOS</th>
                <th>TEMPO GASTO</th>
                <th>CUSTO INSUMOS</th>
                <th>CUSTO MAQUINA</th>
                <th>CUSTO MAO OBRA</th>
                <th>CUSTO TOTAL</th>
              </tr>
            </thead>
            <tbody>
              ${dados.map(item => `
                <tr>
                  <td>${item.data || '-'}</td>
                  <td>${item.atividade || '-'}</td>
                  <td>${item.talhao || '-'}</td>
                  <td>${item.recursos || '-'}</td>
                  <td>${item.tempo_gasto || '-'}</td>
                  <td>${formatarMoeda(item.custo_insumos)}</td>
                  <td>${formatarMoeda(item.custo_maquina)}</td>
                  <td>${formatarMoeda(item.custo_mao_obra)}</td>
                  <td><strong>${formatarMoeda(item.custo_total)}</strong></td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="footer">Sistema de Gestao Agropecuaria - Relatorio Gerado Automaticamente</div>
          <script>
            window.onload = () => { window.print(); setTimeout(() => window.close(), 100); }
          <\/script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Componente de Select Multiplo
  const MultiSelect = ({ label, options, selectedValues, fieldName, refProp }) => {
    const optionsArray = Array.isArray(options) ? options : [];
    const selectedArray = Array.isArray(selectedValues) ? selectedValues : [];

    const getSelectedNames = () => {
      const selected = optionsArray.filter(opt => selectedArray.includes(opt.id));
      if (selected.length === 0) return `Selecione ${label.toLowerCase()}`;
      if (selected.length <= 2) return selected.map(s => s.nome || s.nome_codigo).join(", ");
      return `${selected.length} ${label.toLowerCase()} selecionados`;
    };

    return (
      <div className="relative" ref={refProp}>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {label}
        </label>
        <div
          onClick={() => setOpenDropdown(openDropdown === fieldName ? null : fieldName)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer focus:ring-2 focus:ring-brand-green focus:border-transparent"
        >
          <span className="text-gray-700">{getSelectedNames()}</span>
          <span className="float-right">▼</span>
        </div>

        {openDropdown === fieldName && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            <div className="p-2 border-b border-gray-200 bg-gray-50">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearAll(fieldName);
                }}
                className="text-sm text-blue-600 hover:text-blue-800 mr-3"
              >
                Limpar todos
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectAll(fieldName, optionsArray);
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Selecionar todos
              </button>
            </div>
            {optionsArray.length === 0 ? (
              <div className="px-3 py-2 text-gray-500 text-sm">
                Nenhuma opção disponível
              </div>
            ) : (
              optionsArray.map((option) => (
                <label
                  key={option.id}
                  className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedArray.includes(option.id)}
                    onChange={() => handleMultiSelect(fieldName, option.id)}
                    className="mr-3 h-4 w-4 text-brand-green rounded border-gray-300"
                  />
                  <span className="text-gray-700">{option.nome || option.nome_codigo}</span>
                </label>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <CabecalhoRelatorio
            icone={MdAttachMoney}
            titulo="Relatório de Custo por Atividade"
            descricao="Consulte os custos detalhados por atividade agrícola"
          />

          {/* Filtros */}
          <Cartao titulo="Filtros" icone={FiFilter} className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Tipos de Atividade - Multi Select */}
              <MultiSelect
                label="Tipos de Atividade"
                options={tiposAtividade}
                selectedValues={filtros.tiposAtividade}
                fieldName="tiposAtividade"
                refProp={dropdownRefs.tiposAtividade}
              />

              {/* Talhoes - Multi Select */}
              <MultiSelect
                label="Talhoes"
                options={talhoes}
                selectedValues={filtros.talhoes}
                fieldName="talhoes"
                refProp={dropdownRefs.talhoes}
              />

              {/* Fazendas - Multi Select */}
              <MultiSelect
                label="Fazendas"
                options={fazendas}
                selectedValues={filtros.fazendas}
                fieldName="fazendas"
                refProp={dropdownRefs.fazendas}
              />

              {/* Data Inicio */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Data Inicio
                </label>
                <input
                  type="date"
                  name="data_inicio"
                  value={filtros.data_inicio}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                />
              </div>

              {/* Data Fim */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Data Fim
                </label>
                <input
                  type="date"
                  name="data_fim"
                  value={filtros.data_fim}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                />
              </div>
            </div>

            {/* Filtros Ativos - Tags */}
            {((filtros.tiposAtividade && filtros.tiposAtividade.length > 0) ||
              (filtros.talhoes && filtros.talhoes.length > 0) ||
              (filtros.fazendas && filtros.fazendas.length > 0)) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Filtros Selecionados
                </label>
                <div className="flex flex-wrap gap-2">
                  {filtros.tiposAtividade && filtros.tiposAtividade.map((id) => {
                    const tipo = tiposAtividade.find(t => t.id === id);
                    return tipo ? (
                      <span key={`tipo-${id}`} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Atividade: {tipo.nome}
                        <button
                          onClick={() => handleMultiSelect("tiposAtividade", id)}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                  {filtros.talhoes && filtros.talhoes.map((id) => {
                    const talhao = talhoes.find(t => t.id === id);
                    return talhao ? (
                      <span key={`talhao-${id}`} className="bg-green-100 text-green-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Talhao: {talhao.nome_codigo}
                        <button
                          onClick={() => handleMultiSelect("talhoes", id)}
                          className="ml-1 text-green-600 hover:text-green-800"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                  {filtros.fazendas && filtros.fazendas.map((id) => {
                    const fazenda = fazendas.find(f => f.id === id);
                    return fazenda ? (
                      <span key={`fazenda-${id}`} className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Fazenda: {fazenda.nome}
                        <button
                          onClick={() => handleMultiSelect("fazendas", id)}
                          className="ml-1 text-purple-600 hover:text-purple-800"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {erro && (
              <div className="mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                <FiAlertCircle size={18} className="shrink-0" />
                <span>{erro}</span>
              </div>
            )}

            {/* Botoes */}
            <div className="flex gap-3 mt-6">
              <BotaoRelatorio
                variante="primario"
                onClick={buscarDados}
                carregando={carregando}
                className="flex-1"
              >
                Gerar Relatório
              </BotaoRelatorio>
              <BotaoRelatorio variante="secundario" onClick={limparFiltros}>
                Limpar Filtros
              </BotaoRelatorio>
            </div>
          </Cartao>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            overflow: 'auto'
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl my-8 flex flex-col"
            style={{
              zIndex: 10000,
              maxHeight: 'calc(100vh - 4rem)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <MdAttachMoney size={22} className="text-brand-green" />
                Relatório de Custo por Atividade
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition flex-shrink-0"
              >
                <FiX size={18} className="text-gray-600" />
              </button>
            </div>

            {/* Acoes */}
            <div className="p-6 border-b border-gray-200 bg-gray-50 flex flex-wrap gap-3 flex-shrink-0">
              <BotaoRelatorio variante="excel" onClick={exportarExcel}>
                Exportar para Excel
              </BotaoRelatorio>
              <BotaoRelatorio variante="pdf" onClick={exportarPDF}>
                Exportar para PDF
              </BotaoRelatorio>
              <BotaoRelatorio variante="imprimir" onClick={imprimir}>
                Imprimir
              </BotaoRelatorio>
            </div>

            {/* Tabela */}
            <div className="flex-1 overflow-auto p-6" style={{ minHeight: 0 }}>
              {/* Estatísticas */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <CartaoEstatistica
                  rotulo="Total Atividades"
                  valor={dados.length}
                  cor="azul"
                />
                <CartaoEstatistica
                  rotulo="Total Insumos"
                  valor={formatarMoeda(dados.reduce((acc, item) => acc + (parseFloat(item.custo_insumos) || 0), 0))}
                  cor="verde"
                />
                <CartaoEstatistica
                  rotulo="Total Mão de Obra"
                  valor={formatarMoeda(dados.reduce((acc, item) => acc + (parseFloat(item.custo_mao_obra) || 0), 0))}
                  cor="roxo"
                />
                <CartaoEstatistica
                  rotulo="Custo Total"
                  valor={formatarMoeda(dados.reduce((acc, item) => acc + (parseFloat(item.custo_total) || 0), 0))}
                  cor="vermelho"
                />
              </div>

              {dados.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border border-gray-200" style={{ maxWidth: '100%' }}>
                  <div style={{ minWidth: 'max-content' }}>
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200 sticky top-0">
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">DATA</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">ATIVIDADE</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">TALHAO</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">RECURSOS UTILIZADOS</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">TEMPO GASTO</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">CUSTO INSUMOS</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">CUSTO MAQUINA</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">CUSTO MAO OBRA</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">CUSTO TOTAL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dados.map((item, index) => (
                          <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition">
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.data || "-"}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                              <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                                {item.atividade || "-"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.talhao || "-"}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{item.recursos || "-"}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.tempo_gasto || "-"}</td>
                            <td className="px-4 py-3 text-sm text-green-600 whitespace-nowrap">{formatarMoeda(item.custo_insumos)}</td>
                            <td className="px-4 py-3 text-sm text-orange-600 whitespace-nowrap">{formatarMoeda(item.custo_maquina)}</td>
                            <td className="px-4 py-3 text-sm text-purple-600 whitespace-nowrap">{formatarMoeda(item.custo_mao_obra)}</td>
                            <td className="px-4 py-3 text-sm text-red-600 font-bold whitespace-nowrap">{formatarMoeda(item.custo_total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <EstadoVazio
                  titulo="Nenhuma atividade encontrada"
                  descricao="Ajuste os filtros selecionados e tente novamente."
                />
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end flex-shrink-0">
              <BotaoRelatorio variante="secundario" onClick={() => setShowModal(false)}>
                Fechar
              </BotaoRelatorio>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RelatorioCustoPorAtividade;
